import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "components/ui/dialog";
import { Label } from "components/ui/label";
import { Button } from "components/ui/button";
import { Download, Mail } from "lucide-react";
import { useAuth } from "AuthContext";
import { getTmeOrderEmail, sendTmeOrderEmail } from "actions/tmeAction";
import { saveOrder } from "actions/cartActions";
import { buildTmeOrderExcel } from "utils/tmeOrderExcel";
import { getProductPrice, formatPrice } from "utils/cartUtils";
import { getCurrencySymbol } from "utils/searchUtils";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import SamiSpin from "components/SamiSpin";

const MySwal = withReactContent(Swal);

const base64ToBlob = (base64, mime) => {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
};

const TMEModal = ({ open, modelData, setModelData }) => {
  const navigate = useNavigate();
  const { cart, removeByPartNumber } = useAuth();
  const tmeCartItems = useMemo(
    () => cart.filter((item) => item.api_partner?.toLowerCase() === "tme"),
    [cart],
  );

  // Stable order reference for this checkout (used for the file name + record).
  const [orderReference] = useState(`TME-${Date.now()}`);
  const [toEmail, setToEmail] = useState("");
  const [emailLocked, setEmailLocked] = useState(false); // true when configured server-side
  const [subject, setSubject] = useState(`TME Order Request - ${orderReference}`);
  const [body, setBody] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const currencySymbol = getCurrencySymbol(tmeCartItems[0]?.price?.currency);
  const totalUnits = tmeCartItems.reduce((a, it) => a + (parseInt(it.required_qty, 10) || 0), 0);
  const grandTotal = tmeCartItems.reduce(
    (a, it) => a + (parseInt(it.required_qty, 10) || 0) * getProductPrice(it),
    0,
  );

  useEffect(() => {
    if (!open) return;

    // Default body draft.
    setBody(
      `Hello TME Team,\n\n` +
        `Please find attached our order request (${tmeCartItems.length} line item(s), ` +
        `${totalUnits} total units). The full part list with quantities and prices is in the ` +
        `attached Excel file.\n\n` +
        `Kindly confirm availability, pricing and lead time.\n\nThank you.`,
    );

    // Pre-fill the recipient from the configured TME order email.
    (async () => {
      try {
        const res = await getTmeOrderEmail();
        if (res?.status && res?.data?.order_email) {
          setToEmail(res.data.order_email);
          setEmailLocked(true);
        } else {
          setEmailLocked(false);
        }
      } catch (e) {
        setEmailLocked(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    setModelData({ ...modelData, open: false });
    setError(null);
  };

  const handleDownloadExcel = async () => {
    try {
      const { base64, fileName } = await buildTmeOrderExcel(tmeCartItems, { orderReference });
      const blob = base64ToBlob(
        base64,
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Could not generate the Excel file.");
    }
  };

  const handleSend = async () => {
    try {
      setIsLoading(true);
      setError(null);

      if (tmeCartItems.length === 0) {
        setError("No TME items in the cart.");
        setIsLoading(false);
        return;
      }
      if (!emailLocked && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(toEmail).trim())) {
        setError("Enter a valid TME order email, or configure it in distributor settings.");
        setIsLoading(false);
        return;
      }

      const { base64, fileName } = await buildTmeOrderExcel(tmeCartItems, { orderReference });

      const res = await sendTmeOrderEmail({
        subject: subject.trim(),
        body,
        to_email: emailLocked ? undefined : toEmail.trim(),
        order_reference: orderReference,
        file_name: fileName,
        excel_base64: base64,
      });

      if (!res?.status) {
        setError(res?.message || "Failed to send the TME order email.");
        setIsLoading(false);
        return;
      }

      // Record the order in "My Orders" (tme_data + order_items, distributor=TME).
      const bomLinks = tmeCartItems
        .filter((item) => item.bom_item_id)
        .map((item) => ({
          bom_item_id: item.bom_item_id,
          bom_id: item.bom_id ?? null,
          mfr_part_number: item.part_number ?? null,
          part_number: item.api_partner_part_number ?? null,
        }));

      await saveOrder({
        tme_data: {
          order_reference: res.data?.order_reference || orderReference,
          to_email: res.data?.to_email || toEmail,
          subject: subject.trim(),
          cartDetailItems: tmeCartItems.map((item) => ({
            ...item,
            unit_price: getProductPrice(item),
          })),
        },
        bom_links: bomLinks,
      });

      handleClose();
      tmeCartItems.forEach((item) => removeByPartNumber(item.part_number));

      await MySwal.fire({
        title: <strong>TME Order Sent !</strong>,
        html: (
          <i>
            Order emailed to <b>{res.data?.to_email}</b>. Reference{" "}
            <b>{res.data?.order_reference || orderReference}</b>.
          </i>
        ),
        icon: "success",
      });
      navigate("/orders");
    } catch (e) {
      setError(e.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[820px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-primary text-lg font-semibold">
            Send TME Order by Email
          </DialogTitle>
          <DialogDescription>
            TME has no order API - review the draft below and send it to your TME order email. The
            selected parts, quantities and prices are attached as an Excel file.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          {/* Recipient */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tmeToEmail">Order Email (To)</Label>
            <input
              id="tmeToEmail"
              type="email"
              value={toEmail}
              disabled={emailLocked}
              onChange={(e) => setToEmail(e.target.value)}
              placeholder="Enter the TME order email"
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-s360-violet/20 disabled:opacity-70"
            />
            {emailLocked && (
              <span className="text-[11px] text-s360-fg3">
                Configured in distributor settings.
              </span>
            )}
          </div>

          {/* Subject */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tmeSubject">Subject</Label>
            <input
              id="tmeSubject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              className="w-full h-10 px-3 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-s360-violet/20"
            />
          </div>

          {/* Body */}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="tmeBody">Email Body</Label>
            <textarea
              id="tmeBody"
              rows={6}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-s360-violet/20"
            />
          </div>

          {/* Line-item preview */}
          <div className="border border-s360-stroke1 rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-s360-bg2 flex items-center justify-between">
              <span className="text-[12px] font-semibold text-s360-violet-900">
                Attachment preview ({tmeCartItems.length} parts · {totalUnits} units)
              </span>
              <button
                type="button"
                onClick={handleDownloadExcel}
                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-s360-violet hover:underline"
              >
                <Download className="w-3.5 h-3.5" /> Download Excel
              </button>
            </div>
            <div className="overflow-x-auto max-h-52 overflow-y-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[11px] uppercase tracking-[0.06em] text-s360-fg3">
                    <th className="px-3 py-2 text-left">Part Number</th>
                    <th className="px-3 py-2 text-left">Manufacturer</th>
                    <th className="px-3 py-2 text-center">Qty</th>
                    <th className="px-3 py-2 text-right">Unit Price</th>
                    <th className="px-3 py-2 text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {tmeCartItems.map((item, i) => {
                    const unit = getProductPrice(item);
                    const qty = parseInt(item.required_qty, 10) || 0;
                    return (
                      <tr key={i} className="border-t border-s360-bg2">
                        <td className="px-3 py-2 font-mono text-[13px]">{item.part_number}</td>
                        <td className="px-3 py-2 text-[13px]">{item.manufacturer}</td>
                        <td className="px-3 py-2 text-center tabular-nums">{qty}</td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {currencySymbol} {unit}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {currencySymbol} {formatPrice(qty * unit)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t border-s360-stroke1 bg-[#FBFAFF]">
                    <td className="px-3 py-2 font-semibold" colSpan={4}>
                      Grand total
                    </td>
                    <td className="px-3 py-2 text-right font-bold tabular-nums">
                      {currencySymbol} {formatPrice(grandTotal)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-500">
              Error: <span className="text-red-500">{error}</span>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="secondary"
            className="hover:bg-gray-300 border border-gray-300"
            type="button"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button variant="primary" type="button" disabled={isLoading} onClick={handleSend}>
            {isLoading ? <SamiSpin className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
            Send Order Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TMEModal;
